import React, { createContext, useContext, useState } from 'react';

export type TabItem = {
  key: string;
  title: string;
  content: React.ReactNode;
};

type TabContextType = {
  tabs: TabItem[];
  activeKey: string;
  addTab: (tab: TabItem) => void;
  removeTab: (key: string) => void;
  setActiveKey: (key: string) => void;
  setTabs: (tabs: TabItem[]) => void;
};

const TabContext = createContext<TabContextType | undefined>(undefined);

export const TabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [activeKey, setActiveKey] = useState('/dashboard');

  const addTab = (tab: TabItem) => {
    setTabs((prev) => {
      if (prev.find((t) => t.key === tab.key)) return prev;
      return [...prev, tab];
    });
    setActiveKey(tab.key);
  };

  const removeTab = (key: string) => {
    setTabs((prev) => prev.filter((t) => t.key !== key));
  };

  return (
    <TabContext.Provider
      value={{
        tabs,
        activeKey,
        addTab,
        removeTab,
        setActiveKey,
        setTabs,
      }}
    >
      {children}
    </TabContext.Provider>
  );
};

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return context;
};
